<div class="inmate-details-container">
  <forge-scaffold>
    <forge-toolbar slot="header">
      <h1 id="dialog-title" slot="start">Inmate record</h1>
      <forge-icon-button slot="end" aria-label="Close dialog" (click)="closeDialog()">
        <forge-icon name="close"></forge-icon>
      </forge-icon-button>
    </forge-toolbar>

    <div slot="body" class="inmate-details-body">
      <div class="inmate-detail-banner">
        <img [src]="mugshotImageDetails?.imageUrls?.size320 ?? 'assets/no-image-headshot.png'"
          alt="Mugshot"
          [class.clickable]="!hideMugShots"
          (click)="openEnlargedMugshot()"
          (keydown.enter)="openEnlargedMugshot()"
          (keydown.space)="$event.preventDefault(); openEnlargedMugshot()"
          [attr.tabindex]="hideMugShots ? null : '0'"
          [attr.role]="hideMugShots ? null : 'button'"
          [attr.aria-label]="hideMugShots ? 'Inmate mugshot' : 'View enlarged mugshot'"
          [attr.title]="hideMugShots ? null : 'Click to enlarge'"
          (error)="onImageError()" />
        <div class="inmate-name">{{ inmate.name.fullName }}</div>
        <div><span class="so-label">SO # </span><span class="so-number">{{ inmate.soNumber }}</span></div>
      </div>
      <forge-tab-bar data-aria-label="Inmate Detail Tabs" [activeTab]="activeTabIndex"
        (forge-tab-bar-change)="onTabChange($event)">
        <forge-tab>Details</forge-tab>
        <forge-tab>Charges <forge-badge slot="end" [strong]="activeTabIndex === 1"
            [theme]="activeTabIndex === 1 ? 'info' : 'info-secondary'">{{charges.length}}</forge-badge></forge-tab>
        <forge-tab>Bonds <forge-badge slot="end" [strong]="activeTabIndex === 2"
            [theme]="activeTabIndex === 2 ? 'info' : 'info-secondary'">{{inmate.bonds?.length ??
            0}}</forge-badge></forge-tab>
      </forge-tab-bar>
      <forge-view-switcher [index]="activeTabIndex">
        <forge-view>
          <div class="detail-section">
            <h3 class="forge-typography--heading6">Demographics</h3>
            <forge-divider></forge-divider>
            <div class="demographics">
              <div class="demographics-item">
                <span class="label">Race</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.race }">{{ inmate.race | na }}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Gender</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.gender }">{{ inmate.gender | na }}</span>
              </div>
              <div class="demographics-item" [hidden]="hideDateOfBirth">
                <span class="label">Date of birth</span>
                <span class="value">{{ inmate.dateOfBirth | date:'MMMM dd, yyyy'}}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Age</span>
                <span class="value">{{ inmate.age }}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Hair</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.hairColor }">{{ inmate.hairColor | na }} </span>
              </div>
              <div class="demographics-item">
                <span class="label">Eyes</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.eyeColor }"> {{ inmate.eyeColor | na }} </span>
              </div>
              <div class="demographics-item">
                <span class="label">Weight</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.weight }">{{ inmate.weight ? inmate.weight + '
                  lbs.' : null | na }}</span>
              </div>
              <div class="demographics-item">
                <span class="label">Height</span>
                <span class="value" [ngClass]="{ 'no-value': !inmate.height }">{{ inmate.height | height | na }}</span>
              </div>
              <div class="demographics-item" [hidden]="hideAlias">
                <span class="label">Aliases</span>
                <span class="value" [ngClass]="{ 'no-value': formattedAliases === 'n/a' }">{{ formattedAliases }}</span>
              </div>
              <div class="demographics-item double" [hidden]="hideAddress">
                <span class="label">Address</span>
                <span class="value"
                  [ngClass]="{ 'no-value': (!inmate.address || (!inmate.address.city && !inmate.address.state)) }">{{
                  inmate.address ? (inmate.address.city ?? '') + ', ' + (inmate.address.state ?? '') : '' | na }}</span>
              </div>
            </div>
          </div>
          <div class="detail-section">
            <h3 class="forge-typography--heading6">Booking Details</h3>
            <forge-divider></forge-divider>
            <div class="booking-details">
              <div class="booking-details-item">
                <span class="label">Booking number</span>
                <span class="value">{{ inmate.id }}</span>
              </div>
              <div class="booking-details-item">
                <span class="label">Arrest date / time</span>
                <span class="value">{{ inmate.arrests && inmate.arrests.length > 0 ? (inmate.arrests[0].arrestDate |
                  date:'MMMM dd, yyyy - h:mm a') : undefined | na }}</span>
              </div>
              <div class="booking-details-item">
                <span class="label">Release date / time</span>
                <span class="value no-value">n/a</span>
              </div>
              <div class="booking-details-item">
                <span class="label">Facility</span>
                <span class="value" [ngClass]="{ 'no-value': !facilityName }">{{ facilityName | na }}</span>
              </div>
              <div class="booking-details-item double">
                <span class="label">Holds</span>
                <span class="value" [ngClass]="{ 'no-value': holdsLabel === 'n/a' }">{{ holdsLabel }}</span>
              </div>
              <div class="booking-details-item">
                <span class="label">Arresting agency</span>
                <span class="value">{{ inmate.arrests ? inmate.arrests[0].arrestingAgency : undefined | na }}</span>
              </div>
            </div>
          </div>
        </forge-view>
        <forge-view>
          <h3 class="forge-typography--heading6">Charges ({{charges.length}})</h3>
          <div class="charge-details">
            @for (arrest of inmate.arrests; track arrest) {
            <charge-details *ngFor="let charge of arrest.charges" [arrest]="arrest" [charge]="charge"></charge-details>
            }
          </div>
        </forge-view>
        <forge-view>
          <forge-card class="bond-summary-card">
            <div class="bond-summary-container">
              <div class="bond-summary-row">
                <span class="label">Total bond amount</span>
                <span class="bond-summary-value">{{bondTotalAmount | currency:'USD':'symbol':'1.0-0'}}</span>
              </div>
              @if (displayBondFees) {
                @if (shouldShowContactFacilityInSummary) {
                <div class="bond-summary-row">
                  <span class="label">Bond fees</span>
                  <span class="bond-summary-message">Contact facility for bond fee information</span>
                </div>
                } @else {
                <div class="bond-summary-row">
                  <span class="label">Bond fees</span>
                  <span class="bond-summary-value">{{bondFeesTotal | currency:'USD':'symbol':'1.0-0'}}</span>
                </div>
                <div class="bond-summary-row bond-summary-total">
                  <span class="label">Total amount due</span>
                  <span class="bond-summary-value">{{totalAmountDue | currency:'USD':'symbol':'1.0-0'}}</span>
                </div>
                }
              }
            </div>
          </forge-card>
          <div class="bond-headers">
            <span>Bond type / amount</span>
            <span>Charges</span>
          </div>
          <div class="bond-details">
            <bond-details *ngFor="let bond of inmate.bonds" [bond]="bond" [bondSettings]="bondSettings"></bond-details>
          </div>
        </forge-view>
      </forge-view-switcher>
    </div>
    <div slot="footer" class="footer-action" *ngIf="activeTabIndex === 0 && vineUrl">
      <forge-button variant="outlined" class="notify-btn" (click)="openVineUrl()" (keydown.enter)="openVineUrl()" (keydown.space)="$event.preventDefault(); openVineUrl()">
        <forge-icon name="notifications" slot="start" class="notify-icon"></forge-icon>
        <span class="notify-text">Get notified of inmate's custody status</span>
      </forge-button>
    </div>
  </forge-scaffold>
</div>